// Unified sort utility for SDG goals, targets, and indicators.
//
// SDG identifiers look like:
//   - Goals:      "1", "2", ..., "17"
//   - Targets:    "1.1", "1.2", ..., "1.a", "1.b"
//   - Indicators: "1.1.1", "1.5.2", "1.a.1"
//
// Ordering rules (per Pietro):
//   1. Compare segments left-to-right.
//   2. Within a segment, NUMERIC segments come before LETTER segments
//      (so 1.1 ... 1.9 come before 1.a, 1.b ...).
//   3. Numeric segments are compared numerically (so 1.10 sorts after 1.9, not after 1.1).
//   4. Letter segments are compared case-insensitively.
//
// Expected sort output:
//   1.1, 1.2, 1.3, 1.4, 1.4, 1.5, 1.5, 1.5, 1.a, 1.a, 2.1, 2.1, 2.2, 2.2, ...
//
// Duplicates (multiple indicators sharing the same target index, or
// multiple targets pulled from the same goal) preserve their input order.

interface IndexPart {
  type: 'number' | 'letter';
  value: number | string;
}

/**
 * Anything that carries an SDG identifier somewhere we can find it.
 * Order of precedence when picking the sort key:
 *   acf.index → index → code → acf.code → title_render → title
 */
interface SortableSdgItem {
  acf?: {
    index?: string | number;
    code?: string | number;
    [key: string]: any;
  };
  index?: string | number;
  code?: string | number;
  title_render?: string;
  title?: string;
}

/**
 * Extract the SDG code/index string from an item. Falls back to parsing
 * the leading code from the title (e.g. "1.1.1 No poverty" → "1.1.1",
 * "Goal 1: No poverty" → "1").
 */
const extractIndex = (item: SortableSdgItem | null | undefined): string => {
  if (!item) return '';

  const raw =
    item.acf?.index ??
    item.acf?.code ??
    item.index ??
    item.code ??
    '';

  if (raw !== '' && raw != null) {
    return String(raw).trim();
  }

  const titleSources = [item.title_render, item.title];
  for (const t of titleSources) {
    if (!t) continue;
    const str = String(t).trim();
    // Match leading SDG-style code: digits + optional ".segment" parts, where
    // each segment is digits or letters (e.g. "1", "1.1", "1.a", "1.1.1", "1.a.1").
    const m = str.match(/^(\d+(?:\.[0-9a-z]+)*)/i);
    if (m) return m[1];
    // Fallback: "Goal 1: ...", "Goal 17 – ..."
    const g = str.match(/Goal\s+(\d+)/i);
    if (g) return g[1];
  }

  return '';
};

const parseIndexParts = (index: string): IndexPart[] => {
  if (!index) return [];
  return index
    .split('.')
    .filter((s) => s.length > 0)
    .map((part) => {
      // Treat a part as numeric only if it's *entirely* digits. This avoids
      // accidentally parsing "a1" as a number; SDG codes don't mix in practice.
      if (/^\d+$/.test(part)) {
        return { type: 'number', value: parseInt(part, 10) };
      }
      return { type: 'letter', value: part.toLowerCase() };
    });
};

const compareIndexes = (rawA: string, rawB: string): number => {
  const partsA = parseIndexParts(rawA);
  const partsB = parseIndexParts(rawB);

  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const a = partsA[i];
    const b = partsB[i];

    // Shorter index comes first (e.g. "1" before "1.1").
    if (!a && b) return -1;
    if (a && !b) return 1;
    if (!a && !b) return 0;

    // Numbers before letters within the same segment.
    if (a.type !== b.type) {
      return a.type === 'number' ? -1 : 1;
    }

    if (a.type === 'number') {
      const diff = (a.value as number) - (b.value as number);
      if (diff !== 0) return diff;
    } else {
      const cmp = (a.value as string).localeCompare(b.value as string);
      if (cmp !== 0) return cmp;
    }
  }

  return 0;
};

/**
 * Canonical SDG sort. Works for any mix of goals, targets, and indicators
 * because it relies only on the code/index string.
 *
 * Stable: items with identical sort keys keep their input order (Array.sort
 * is stable in all supported runtimes), which matches the user-provided
 * example where duplicate target indices appear in a stable block.
 */
export const sortBySdgIndex = <T extends SortableSdgItem>(items: T[]): T[] => {
  if (!Array.isArray(items)) return items;
  return [...items].sort((a, b) => compareIndexes(extractIndex(a), extractIndex(b)));
};

// Backwards-compatible named aliases. All three use the same underlying
// algorithm so goals, targets, and indicators sort identically everywhere.
export const sortByIndex = sortBySdgIndex;
export const sortGoals = sortBySdgIndex;
export const sortTargets = sortBySdgIndex;
export const sortIndicators = sortBySdgIndex;

// Exposed for unit tests / debugging.
export const __sortInternals = { extractIndex, parseIndexParts, compareIndexes };
